import { useEffect, useMemo, useState } from "react";
import {
  Car,
  User as UserIcon,
  Plus,
  PencilSimple,
  Trash,
  Clock,
  CheckCircle,
  WarningCircle,
  XCircle,
  Coins,
  ChartBar,
  ArrowLeft,
  Phone,
  EnvelopeSimple,
  IdentificationCard,
  Calendar,
  CurrencyDollar,
  ReceiptX,
  ArrowClockwise,
  Wrench,
  UploadSimple,
  MagnifyingGlass,
  FileXls,
  FilePdf,
  Signature,
  Sparkle,
  Copy,
  UserCircle,
  Users,
  Folders,
} from "@phosphor-icons/react";
import api, { API } from "../../lib/api";
import { toast } from "sonner";
import { fmtMoney } from "../../lib/finance-format";
import SignatureDialog from "./SignatureDialog";

/**
 * Cuentas por Cobrar — expedientes de venta de carros financiados.
 * Muestra listado tipo carpeta (uno por cliente) + detalle con timeline de pagos.
 * Diseño alineado al resto del app: tiles oscuros, headers font-mono uppercase,
 * colores por estatus (verde AL CORRIENTE, ámbar VENCIDO, rojo MOROSO, azul PAGADO).
 */

const STATUS_META = {
  "AL CORRIENTE": { color: "#059669", bg: "bg-emerald-500/12", border: "border-emerald-500/40", text: "text-emerald-300", icon: CheckCircle },
  "VENCIDO":      { color: "#D97706", bg: "bg-amber-500/12",   border: "border-amber-500/40",   text: "text-amber-300",   icon: Clock },
  "MOROSO":       { color: "#DC2626", bg: "bg-red-500/12",     border: "border-red-500/40",     text: "text-red-300",     icon: WarningCircle },
  "PAGADO":       { color: "#3B82F6", bg: "bg-blue-500/12",    border: "border-blue-500/40",    text: "text-blue-300",    icon: CheckCircle },
};

export default function ReceivablesPanel() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [detectOpen, setDetectOpen] = useState(false);
  // Group by client vs show individual dossiers
  const [viewMode, setViewMode] = useState(() =>
    window.localStorage.getItem("cfo_rcv_view") || "client",
  );
  const setViewModePersisted = (v) => {
    setViewMode(v);
    window.localStorage.setItem("cfo_rcv_view", v);
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/finance/receivables");
      setItems(data.items);
      setSummary(data.summary);
    } catch (err) {
      toast.error("No se pudo cargar la cartera");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const selected = useMemo(
    () => items.find((r) => r.receivable_id === selectedId),
    [items, selectedId],
  );

  const seedDemo = async () => {
    if (!window.confirm("Sembrar los 2 clientes demo (Sergio GMC + Mercedes)?")) return;
    try {
      const { data } = await api.post("/finance/receivables/seed-demo");
      toast.success(`${data.receivables} clientes + ${data.payments} pagos`);
      load();
    } catch (err) {
      toast.error("No se pudo sembrar");
    }
  };

  const deleteReceivable = async (r) => {
    if (!window.confirm(`¿Eliminar el expediente de ${r.client_name}? Esto también borra ${r.payment_count} pagos.`)) return;
    try {
      await api.delete(`/finance/receivables/${r.receivable_id}`);
      toast.success("Expediente eliminado");
      if (selectedId === r.receivable_id) setSelectedId(null);
      load();
    } catch (err) {
      toast.error("No se pudo eliminar");
    }
  };

  return (
    <section className="space-y-4" data-testid="receivables-panel">
      {/* Summary strip */}
      <SummaryStrip summary={summary} loading={loading} />

      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#FACC15]">
            ACCOUNTS RECEIVABLE · CARTERA DE CARROS
          </div>
          <h2 className="text-lg font-heading font-bold text-white">
            Por Cobrar · Expedientes
          </h2>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {items.length === 0 && (
            <button
              data-testid="rcv-seed-demo"
              onClick={seedDemo}
              className="text-[10px] font-mono uppercase tracking-widest text-white/50 hover:text-white/80 px-2 py-1.5"
            >
              Sembrar demo
            </button>
          )}
          <button
            data-testid="rcv-detect-btn"
            onClick={() => setDetectOpen(true)}
            className="flex items-center gap-1.5 rounded-lg border border-white/10 bg-black/30 hover:bg-black/50 text-white/80 font-mono uppercase tracking-widest px-3 py-2 text-[10px]"
            title="Detectar pagos desde transacciones importadas"
          >
            <MagnifyingGlass size={11} weight="bold" /> Detectar desde tx
          </button>
          <button
            data-testid="rcv-import-btn"
            onClick={() => setImportOpen(true)}
            className="flex items-center gap-1.5 rounded-lg border border-emerald-500/40 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 font-mono uppercase tracking-widest px-3 py-2 text-[10px]"
            title="Importar un Excel con el formato Cobranza_Carros"
          >
            <FileXls size={11} weight="bold" /> Importar Excel
          </button>
          <button
            data-testid="rcv-new-btn"
            onClick={() => { setEditing(null); setFormOpen(true); }}
            className="on-solid flex items-center gap-1.5 rounded-lg bg-[#FACC15] hover:bg-[#EAB308] text-slate-900 font-heading font-bold px-3 py-2 text-xs"
          >
            <Plus size={12} weight="bold" /> Nuevo cliente
          </button>
        </div>
      </div>

      {/* Two-pane layout: cards list + detail */}
      <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-4">
        <div>
          {/* View mode toggle */}
          {items.length > 0 && (
            <div
              data-testid="rcv-view-toggle"
              className="inline-flex items-center p-0.5 rounded-lg bg-black/40 border border-white/8 mb-2"
            >
              <button
                data-testid="rcv-view-client"
                onClick={() => setViewModePersisted("client")}
                className={`px-2 py-1 text-[10px] font-mono uppercase tracking-widest rounded-md flex items-center gap-1 transition ${
                  viewMode === "client"
                    ? "on-solid bg-[#FACC15] text-slate-900"
                    : "text-white/50 hover:text-white/80"
                }`}
              >
                <Users size={11} weight="fill" /> Por cliente
              </button>
              <button
                data-testid="rcv-view-dossier"
                onClick={() => setViewModePersisted("dossier")}
                className={`px-2 py-1 text-[10px] font-mono uppercase tracking-widest rounded-md flex items-center gap-1 transition ${
                  viewMode === "dossier"
                    ? "on-solid bg-[#FACC15] text-slate-900"
                    : "text-white/50 hover:text-white/80"
                }`}
              >
                <Folders size={11} weight="fill" /> Por expediente
              </button>
            </div>
          )}
          <ClientList
            items={items}
            viewMode={viewMode}
            selectedId={selectedId}
            onSelect={setSelectedId}
            loading={loading}
          />
        </div>
        <div className="min-h-[400px]">
          {selected ? (
            <ClientDetail
              receivable={selected}
              onBack={() => setSelectedId(null)}
              onEdit={() => { setEditing(selected); setFormOpen(true); }}
              onDelete={() => deleteReceivable(selected)}
              onChanged={load}
            />
          ) : (
            <EmptyState hasItems={items.length > 0} />
          )}
        </div>
      </div>

      {formOpen && (
        <ReceivableFormDialog
          editing={editing}
          onClose={() => setFormOpen(false)}
          onSaved={() => { setFormOpen(false); load(); }}
        />
      )}

      {importOpen && (
        <ImportXlsxDialog
          onClose={() => setImportOpen(false)}
          onImported={() => { setImportOpen(false); load(); }}
        />
      )}

      {detectOpen && (
        <DetectFromTxDialog
          onClose={() => setDetectOpen(false)}
          onCreated={() => { setDetectOpen(false); load(); }}
        />
      )}
    </section>
  );
}


// ============================================================ SummaryStrip
function SummaryStrip({ summary, loading }) {
  if (loading && !summary) return <div className="h-24 rounded-lg border border-white/8 bg-black/20 animate-pulse" />;
  if (!summary) return null;
  const cards = [
    { label: "Por cobrar",       value: fmtMoney(summary.total_outstanding), icon: Coins,          color: "#EF4444", testid: "rcv-summary-outstanding" },
    { label: "Cobrado a la fecha", value: fmtMoney(summary.total_collected),  icon: CheckCircle,   color: "#10B981", testid: "rcv-summary-collected" },
    { label: "Esperado / mes",   value: fmtMoney(summary.monthly_expected),  icon: Calendar,       color: "#3B82F6", testid: "rcv-summary-monthly" },
    { label: "Clientes activos", value: `${summary.count_active} / ${summary.count}`, icon: UserIcon, color: "#FACC15", testid: "rcv-summary-active" },
    { label: "Con atraso",       value: `${summary.count_overdue}`,          icon: WarningCircle,  color: "#DC2626", testid: "rcv-summary-overdue" },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
      {cards.map((c) => (
        <div
          key={c.label}
          data-testid={c.testid}
          className="rounded-lg border border-white/8 bg-black/20 p-3"
        >
          <div className="flex items-center justify-between mb-1">
            <div className="text-[9px] font-mono uppercase tracking-widest text-white/40">
              {c.label}
            </div>
            <c.icon size={13} weight="fill" style={{ color: c.color }} />
          </div>
          <div className="text-lg font-heading font-bold text-white tabular-nums">
            {c.value}
          </div>
        </div>
      ))}
    </div>
  );
}


// ============================================================ ClientList
function ClientList({ items, viewMode, selectedId, onSelect, loading }) {
  if (loading && items.length === 0) {
    return <div className="rounded-lg border border-white/8 bg-black/20 p-6 text-xs font-mono text-white/40 text-center">Cargando...</div>;
  }
  if (items.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-white/10 bg-black/10 p-6 text-center">
        <Car size={32} weight="fill" className="mx-auto mb-2 text-white/20" />
        <div className="text-xs font-mono text-white/40">Aún no hay expedientes.</div>
        <div className="text-[10px] font-mono text-white/30 mt-1">Crea uno con &quot;Nuevo cliente&quot; o siembra los demo.</div>
      </div>
    );
  }
  // Group receivables by normalized client name when in client-view mode
  const groups = viewMode === "client" ? groupByClient(items) : items.map((r) => ({
    key: r.receivable_id, name: r.client_name, receivables: [r],
  }));
  return (
    <div className="space-y-2 max-h-[70vh] overflow-y-auto pr-1" data-testid="rcv-client-list">
      {groups.map((g) =>
        g.receivables.length > 1 ? (
          <ClientGroupCard
            key={g.key}
            group={g}
            selectedId={selectedId}
            onSelect={onSelect}
          />
        ) : (
          <ClientCard
            key={g.receivables[0].receivable_id}
            r={g.receivables[0]}
            active={g.receivables[0].receivable_id === selectedId}
            onClick={() => onSelect(g.receivables[0].receivable_id)}
          />
        ),
      )}
    </div>
  );
}

function normalizeClientName(name) {
  return (name || "")
    .toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s]/g, " ")
    .trim();
}

function groupByClient(items) {
  const buckets = new Map();
  for (const r of items) {
    const norm = normalizeClientName(r.client_name);
    // Group by first token (first name) — Sergio + Sergio Alvarez → same bucket
    const key = norm.split(/\s+/)[0] || norm || "sin-nombre";
    if (!buckets.has(key)) buckets.set(key, { key, name: r.client_name, receivables: [] });
    buckets.get(key).receivables.push(r);
  }
  return Array.from(buckets.values())
    .sort((a, b) => b.receivables.length - a.receivables.length || a.name.localeCompare(b.name));
}

function ClientGroupCard({ group, selectedId, onSelect }) {
  const rcvs = group.receivables;
  const totalBalance = rcvs.reduce((s, r) => s + (r.current_balance || 0), 0);
  const totalCollected = rcvs.reduce((s, r) => s + (r.total_collected || 0), 0);
  const worst = rcvs.reduce((worst, r) => {
    const order = { PAGADO: 0, "AL CORRIENTE": 1, VENCIDO: 2, MOROSO: 3 };
    return (order[r.computed_status] ?? 0) > (order[worst?.computed_status] ?? -1) ? r : worst;
  }, rcvs[0]);
  const meta = STATUS_META[worst.computed_status] || STATUS_META["AL CORRIENTE"];
  return (
    <div
      data-testid={`rcv-group-${group.key}`}
      className="rounded-lg border border-[#FACC15]/30 bg-[#FACC15]/6 p-3 space-y-2"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 min-w-0">
          <Users size={12} weight="fill" className="text-[#FACC15] shrink-0" />
          <span className="text-[13px] font-heading font-bold text-white truncate">
            {group.name.split(/\s+/)[0].charAt(0).toUpperCase() + group.name.split(/\s+/)[0].slice(1).toLowerCase()}
          </span>
          <span className="shrink-0 px-1.5 py-0.5 rounded bg-[#FACC15]/20 text-[#FACC15] text-[9px] font-mono font-bold">
            {rcvs.length} carros
          </span>
        </div>
        <span className={`shrink-0 px-1.5 py-0.5 rounded ${meta.bg} ${meta.text} ${meta.border} border text-[8px] font-mono font-bold tracking-widest`}>
          {worst.computed_status}
        </span>
      </div>
      {/* Aggregate */}
      <div className="flex items-center justify-between text-[10px] font-mono">
        <span className="text-white/40">Balance total</span>
        <span className="text-white font-bold tabular-nums">${totalBalance.toFixed(2)}</span>
      </div>
      <div className="flex items-center justify-between text-[10px] font-mono">
        <span className="text-white/40">Cobrado total</span>
        <span className="text-emerald-300 tabular-nums">+${totalCollected.toFixed(2)}</span>
      </div>
      {/* Nested cars */}
      <div className="pt-2 border-t border-white/8 space-y-1.5">
        {rcvs.map((r) => (
          <button
            key={r.receivable_id}
            data-testid={`rcv-sub-${r.receivable_id}`}
            onClick={() => onSelect(r.receivable_id)}
            className={`w-full text-left rounded-md border p-2 transition ${
              r.receivable_id === selectedId
                ? "border-[#FACC15]/60 bg-[#FACC15]/8"
                : "border-white/6 bg-black/20 hover:border-white/16"
            }`}
          >
            <div className="flex items-center justify-between gap-1.5">
              <div className="flex items-center gap-1 text-[11px] text-white/80 truncate">
                <Car size={9} weight="bold" />
                {r.vehicle_year || ""} {r.vehicle_make} {r.vehicle_model}
              </div>
              <span className="text-[10px] font-mono text-white tabular-nums shrink-0">
                ${r.current_balance.toFixed(0)}
              </span>
            </div>
            <div className="h-0.5 rounded-full bg-white/8 mt-1 overflow-hidden">
              <div
                className="h-full rounded-full"
                style={{
                  width: `${Math.min(100, r.pct_paid || 0)}%`,
                  backgroundColor: (STATUS_META[r.computed_status] || STATUS_META["AL CORRIENTE"]).color,
                }}
              />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function ClientCard({ r, active, onClick }) {
  const meta = STATUS_META[r.computed_status] || STATUS_META["AL CORRIENTE"];
  const StatusIcon = meta.icon;
  return (
    <button
      data-testid={`rcv-card-${r.receivable_id}`}
      onClick={onClick}
      className={`w-full text-left rounded-lg border p-3 transition ${
        active
          ? "border-[#FACC15]/50 bg-[#FACC15]/6 shadow-lg"
          : `border-white/8 bg-black/20 hover:border-white/16 hover:bg-black/30`
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <UserIcon size={11} weight="bold" className="text-white/40 shrink-0" />
            <div className="text-[13px] font-heading font-bold text-white truncate">
              {r.client_name}
            </div>
          </div>
          <div className="text-[10px] font-mono text-white/50 mt-0.5 flex items-center gap-1">
            <Car size={9} weight="bold" />
            {r.vehicle_year || ""} {r.vehicle_make} {r.vehicle_model}
          </div>
        </div>
        <span
          className={`shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${meta.bg} ${meta.text} ${meta.border} border text-[8px] font-mono font-bold tracking-widest`}
        >
          <StatusIcon size={9} weight="fill" />
          {r.computed_status}
        </span>
      </div>

      {/* Balance + progress */}
      <div className="mt-3">
        <div className="flex items-center justify-between text-[10px] font-mono">
          <span className="text-white/40">Balance</span>
          <span className="text-white tabular-nums font-bold">{fmtMoney(r.current_balance)}</span>
        </div>
        {/* progress bar */}
        <div className="h-1 rounded-full bg-white/8 mt-1 overflow-hidden">
          <div
            className="h-full rounded-full transition-all"
            style={{
              width: `${Math.min(100, r.pct_paid || 0)}%`,
              backgroundColor: meta.color,
            }}
          />
        </div>
        <div className="flex items-center justify-between text-[9px] font-mono text-white/40 mt-1">
          <span>{r.pct_paid}% pagado</span>
          {r.days_overdue > 0 ? (
            <span className="text-red-300">· {r.days_overdue} días atraso</span>
          ) : r.next_due_date ? (
            <span>Próx: {r.next_due_date}</span>
          ) : null}
        </div>
      </div>
    </button>
  );
}


// ============================================================ EmptyState
function EmptyState({ hasItems }) {
  return (
    <div className="h-full rounded-lg border border-dashed border-white/10 bg-black/10 flex items-center justify-center">
      <div className="text-center">
        <Car size={40} weight="fill" className="mx-auto mb-2 text-white/15" />
        <div className="text-xs font-mono text-white/40">
          {hasItems ? "Selecciona un expediente" : "Aún no hay expedientes"}
        </div>
      </div>
    </div>
  );
}


// ============================================================ ClientDetail
function ClientDetail({ receivable, onBack, onEdit, onDelete, onChanged }) {
  const r = receivable;
  const meta = STATUS_META[r.computed_status] || STATUS_META["AL CORRIENTE"];
  const [payments, setPayments] = useState([]);
  const [payLoading, setPayLoading] = useState(true);
  const [payFormOpen, setPayFormOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [signatureOpen, setSignatureOpen] = useState(false);
  const [signatureStatus, setSignatureStatus] = useState(null);

  const loadPayments = async () => {
    setPayLoading(true);
    try {
      const { data } = await api.get(`/finance/receivables/${r.receivable_id}/payments`);
      setPayments(data.items);
    } finally {
      setPayLoading(false);
    }
  };

  const loadSignatureStatus = async () => {
    try {
      const { data } = await api.get(`/finance/receivables/${r.receivable_id}/signature`);
      setSignatureStatus(data.signature || null);
    } catch (err) {
      setSignatureStatus(null);
    }
  };

  useEffect(() => { loadPayments(); loadSignatureStatus(); }, [r.receivable_id]);

  const removePayment = async (p) => {
    if (!window.confirm(`¿Eliminar el pago #${p.seq} de ${fmtMoney(p.received_amount)}?`)) return;
    try {
      await api.delete(`/finance/receivables/${r.receivable_id}/payments/${p.payment_id}`);
      toast.success("Pago eliminado");
      loadPayments();
      onChanged?.();
    } catch (err) {
      toast.error("No se pudo eliminar");
    }
  };

  const sharePaymentReceipt = async (p) => {
    // Mint (or reuse) a public receipt token, copy the URL, and let the user
    // paste it wherever they want (WhatsApp Web / SMS / email).
    try {
      const { data } = await api.post(
        `/finance/receivables/${r.receivable_id}/payments/${p.payment_id}/receipt-link`,
      );
      await navigator.clipboard.writeText(data.share_url);
      toast.success("Enlace del recibo copiado", {
        description: data.share_url,
        duration: 6000,
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo generar el enlace");
    }
  };

  const shareClientPortal = async () => {
    // One link per CLIENT (grouped by first name) → shows all their vehicles,
    // saldos and recibos. Idempotent within the 1-year TTL, so pressing it
    // repeatedly returns the same URL.
    const clientKey = normalizeClientName(r.client_name).split(/\s+/)[0] || "sin-nombre";
    try {
      const { data } = await api.post(
        `/finance/clients/${encodeURIComponent(clientKey)}/portal-link`,
      );
      await navigator.clipboard.writeText(data.share_url);
      toast.success("Portal del cliente copiado", {
        description: data.share_url,
        duration: 6000,
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo generar el portal");
    }
  };

  return (
    <div className="rounded-lg border border-white/8 bg-black/20 p-4" data-testid="rcv-detail-panel">
      {/* Header */}
      <div className="flex items-start justify-between gap-2 mb-4">
        <div className="min-w-0 flex-1">
          <button
            onClick={onBack}
            className="lg:hidden text-[10px] font-mono uppercase tracking-widest text-white/50 hover:text-white flex items-center gap-1 mb-1"
          >
            <ArrowLeft size={10} weight="bold" /> Volver
          </button>
          <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#FACC15]">
            EXPEDIENTE · {r.contract_number || r.receivable_id}
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <h3 className="text-xl font-heading font-bold text-white truncate">
              {r.client_name}
            </h3>
            <span
              className={`shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded ${meta.bg} ${meta.text} ${meta.border} border text-[9px] font-mono font-bold tracking-widest`}
            >
              <meta.icon size={10} weight="fill" /> {r.computed_status}
            </span>
          </div>
          <div className="text-[11px] font-mono text-white/60 mt-1 flex items-center gap-1">
            <Car size={11} weight="bold" />
            {r.vehicle_year} {r.vehicle_make} {r.vehicle_model}
            {r.vehicle_color && <span className="text-white/40"> · {r.vehicle_color}</span>}
            {r.vehicle_vin && <span className="text-white/30"> · VIN {r.vehicle_vin}</span>}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            data-testid="rcv-auto-attach-btn"
            onClick={async () => {
              try {
                const { data } = await api.post("/finance/receivables/detect-from-transactions", {
                  pattern: r.client_name.split(" ")[0],
                  min_amount: 10,
                  auto_create: true,
                  receivable_id: r.receivable_id,
                });
                if (data.created?.payments_created > 0) {
                  toast.success(`${data.created.payments_created} pagos vinculados desde transacciones`);
                  onChanged?.();
                  loadPayments();
                } else {
                  toast.info("Sin nuevas transacciones coincidentes");
                }
              } catch (err) {
                toast.error("No se pudo escanear");
              }
            }}
            className="flex items-center gap-1 px-2 py-1.5 rounded-md bg-white/5 hover:bg-white/12 text-white/60 hover:text-white text-[10px] font-mono uppercase tracking-widest transition"
            title="Escanear tus transacciones y vincular pagos coincidentes"
          >
            <MagnifyingGlass size={11} weight="bold" /> Escanear tx
          </button>
          <a
            data-testid="rcv-pdf-btn"
            href={`${API}/finance/receivables/${r.receivable_id}/contract.pdf`}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1 px-2 py-1.5 rounded-md bg-white/5 hover:bg-white/12 text-white/60 hover:text-white text-[10px] font-mono uppercase tracking-widest transition"
            title="Descargar contrato PDF"
          >
            <FilePdf size={11} weight="bold" /> PDF
          </a>
          <button
            data-testid="rcv-signature-btn"
            onClick={() => setSignatureOpen(true)}
            className={`flex items-center gap-1 px-2 py-1.5 rounded-md text-[10px] font-mono uppercase tracking-widest font-bold transition ${
              signatureStatus?.status === "signed"
                ? "bg-emerald-500/20 border border-emerald-500/50 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-500/30"
                : "bg-[#FACC15]/20 border border-[#FACC15]/50 text-amber-700 dark:text-[#FACC15] hover:bg-[#FACC15]/30"
            }`}
            title={signatureStatus?.status === "signed" ? "Contrato firmado — ver firma" : "Enviar contrato al cliente para firma"}
          >
            <Signature size={12} weight="fill" />
            {signatureStatus?.status === "signed" ? "Firmado" : "Enviar a firma"}
          </button>
          <button
            data-testid="rcv-portal-btn"
            onClick={shareClientPortal}
            className="flex items-center gap-1 px-2 py-1.5 rounded-md bg-emerald-500/15 border border-emerald-500/40 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-500/25 text-[10px] font-mono uppercase tracking-widest font-bold transition"
            title="Copiar el link del portal del cliente (todos sus recibos + saldos)"
          >
            <UserCircle size={12} weight="fill" />
            Portal cliente
          </button>
          <button
            data-testid="rcv-edit-btn"
            onClick={onEdit}
            className="p-1.5 rounded-md bg-white/5 hover:bg-white/12 text-white/60 hover:text-white transition"
            title="Editar expediente"
          >
            <PencilSimple size={12} weight="bold" />
          </button>
          <button
            data-testid="rcv-delete-btn"
            onClick={onDelete}
            className="p-1.5 rounded-md bg-white/5 hover:bg-red-500/20 text-white/60 hover:text-red-400 transition"
            title="Eliminar expediente"
          >
            <Trash size={12} weight="bold" />
          </button>
        </div>
      </div>

      {/* Big metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
        <Metric label="Balance"        value={fmtMoney(r.current_balance)} highlight={r.current_balance > 0.01} />
        <Metric label="% Pagado"       value={`${r.pct_paid}%`} />
        <Metric label="Cobrado total"  value={fmtMoney(r.total_collected)} />
        <Metric label="Contrato total" value={fmtMoney(r.total_contract)} />
      </div>

      {/* Contract details */}
      <div className="rounded-md border border-white/6 bg-black/20 p-3 mb-4">
        <div className="text-[9px] font-mono uppercase tracking-widest text-white/40 mb-2">
          Términos del contrato
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-2 text-[11px] font-mono">
          <KV label="Fecha venta"        value={r.sale_date || "—"} />
          <KV label="Precio venta"       value={fmtMoney(r.sale_price)} />
          <KV label="Enganche"           value={fmtMoney(r.down_payment)} />
          <KV label="Saldo inicial"      value={fmtMoney(r.initial_balance)} />
          <KV label="Pago pactado"       value={fmtMoney(r.agreed_payment) + " / " + freqLabel(r.payment_frequency)} />
          <KV label="Plazo"              value={`${r.term_months} pagos`} />
          <KV label="APR"                value={r.apr ? `${r.apr}%` : "—"} />
          <KV label="Próx. vencimiento"  value={r.next_due_date || "—"} highlight={r.days_overdue > 0} />
        </div>
      </div>

      {/* Client contact — collapsible detail */}
      {(r.client_phone || r.client_email || r.client_address) && (
        <div className="rounded-md border border-white/6 bg-black/20 p-3 mb-4">
          <div className="text-[9px] font-mono uppercase tracking-widest text-white/40 mb-2">
            Contacto del cliente
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-2 text-[11px] font-mono">
            {r.client_phone && <ContactRow icon={Phone}              value={r.client_phone} href={`tel:${r.client_phone}`} />}
            {r.client_email && <ContactRow icon={EnvelopeSimple}     value={r.client_email} href={`mailto:${r.client_email}`} />}
            {r.client_id_number && <ContactRow icon={IdentificationCard} value={r.client_id_number} />}
          </div>
        </div>
      )}

      {/* Payment log */}
      <div className="rounded-md border border-white/6 bg-black/20 p-3">
        <div className="flex items-center justify-between mb-2 gap-2">
          <div className="text-[9px] font-mono uppercase tracking-widest text-white/40">
            Registro de pagos · {payments.length} movimientos
          </div>
          <div className="flex items-center gap-1.5">
            <a
              data-testid="rcv-payments-pdf-btn"
              href={`${API}/finance/receivables/${r.receivable_id}/payments.pdf`}
              target="_blank"
              rel="noreferrer"
              className={`flex items-center gap-1 px-2 py-1 rounded-md border text-[10px] font-mono font-bold uppercase tracking-widest transition ${
                payments.length === 0
                  ? "border-white/8 bg-white/2 text-white/25 pointer-events-none"
                  : "border-white/10 bg-white/5 hover:bg-white/12 text-white/70 hover:text-white"
              }`}
              title="Descargar estado de cuenta en PDF"
            >
              <FilePdf size={11} weight="bold" /> Imprimir PDF
            </a>
            <button
              data-testid="rcv-add-payment"
              onClick={() => { setEditingPayment(null); setPayFormOpen(true); }}
              className="on-solid flex items-center gap-1 rounded bg-emerald-700 hover:bg-emerald-800 text-white text-[10px] font-mono font-bold uppercase tracking-widest px-2 py-1"
            >
              <Plus size={9} weight="bold" /> Registrar pago
            </button>
          </div>
        </div>
        {payLoading ? (
          <div className="py-6 text-center text-[10px] font-mono text-white/30">Cargando pagos...</div>
        ) : payments.length === 0 ? (
          <div className="py-6 text-center text-[10px] font-mono text-white/30">Sin pagos registrados aún.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[11px] font-mono">
              <thead>
                <tr className="text-white/40 uppercase text-[9px] tracking-widest border-b border-white/8">
                  <th className="text-left py-1.5 pr-2 w-8">#</th>
                  <th className="text-left py-1.5 pr-2">Fecha pago</th>
                  <th className="text-right py-1.5 pr-2">Recibido</th>
                  <th className="text-right py-1.5 pr-2">Cargo</th>
                  <th className="text-left py-1.5 pr-2">Método</th>
                  <th className="text-left py-1.5 pr-2">Ref.</th>
                  <th className="text-left py-1.5 pr-2">Notas</th>
                  <th className="w-16 text-right pr-2">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p) => (
                  <tr key={p.payment_id} data-testid={`rcv-payment-row-${p.payment_id}`} className="border-b border-white/4 hover:bg-white/2 group align-top">
                    <td className="py-2 pr-2 text-white/40">{p.seq}</td>
                    <td className="py-2 pr-2 text-white whitespace-nowrap">{p.payment_date}</td>
                    <td className="py-2 pr-2 text-right text-emerald-300 tabular-nums whitespace-nowrap">+{fmtMoney(p.received_amount)}</td>
                    <td className="py-2 pr-2 text-right text-amber-300 tabular-nums whitespace-nowrap">{p.extra_charge > 0 ? fmtMoney(p.extra_charge) : ""}</td>
                    <td className="py-2 pr-2 text-white/70 whitespace-nowrap">{p.method || "—"}</td>
                    <td
                      className="py-2 pr-2 text-white/40 break-words"
                      title={p.reference || ""}
                    >
                      {p.reference || "—"}
                    </td>
                    <td
                      className="py-2 pr-2 text-white/60 whitespace-pre-wrap break-words leading-snug"
                      title={p.notes || ""}
                    >
                      {p.notes || ""}
                    </td>
                    <td className="py-2 pr-2 w-20">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          data-testid={`rcv-payment-share-${p.payment_id}`}
                          onClick={() => sharePaymentReceipt(p)}
                          className="p-1 rounded text-white/30 hover:text-emerald-300 hover:bg-white/6 transition"
                          title="Copiar link del recibo"
                        >
                          <Copy size={11} weight="bold" />
                        </button>
                        <button
                          data-testid={`rcv-payment-edit-${p.payment_id}`}
                          onClick={() => { setEditingPayment(p); setPayFormOpen(true); }}
                          className="p-1 rounded text-white/30 hover:text-[#FACC15] hover:bg-white/6 transition"
                          title="Editar pago"
                        >
                          <PencilSimple size={11} weight="bold" />
                        </button>
                        <button
                          data-testid={`rcv-payment-delete-${p.payment_id}`}
                          onClick={() => removePayment(p)}
                          className="p-1 rounded text-white/30 hover:text-red-400 hover:bg-white/6 transition"
                          title="Eliminar pago"
                        >
                          <Trash size={11} weight="bold" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {payFormOpen && (
        <PaymentFormDialog
          receivable={r}
          editing={editingPayment}
          onClose={() => { setPayFormOpen(false); setEditingPayment(null); }}
          onSaved={(opts) => {
            // Refresh the list; only close the dialog if the child didn't ask
            // us to keep it open (used by the "receipt ready" step).
            loadPayments();
            onChanged?.();
            if (!opts?.keepOpen) {
              setPayFormOpen(false);
              setEditingPayment(null);
            }
          }}
        />
      )}

      {signatureOpen && (
        <SignatureDialog
          receivable={r}
          onClose={() => { setSignatureOpen(false); loadSignatureStatus(); }}
          onSigned={() => { loadSignatureStatus(); onChanged?.(); }}
        />
      )}
    </div>
  );
}


function Metric({ label, value, highlight }) {
  return (
    <div className={`rounded-md p-2 ${highlight ? "border border-red-500/40 bg-red-500/8" : "border border-white/8 bg-black/10"}`}>
      <div className="text-[8px] font-mono uppercase tracking-widest text-white/40">{label}</div>
      <div className={`text-base font-heading font-bold tabular-nums ${highlight ? "text-red-300" : "text-white"}`}>
        {value}
      </div>
    </div>
  );
}

function KV({ label, value, highlight }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-widest text-white/40">{label}</div>
      <div className={`text-[12px] font-bold ${highlight ? "text-red-300" : "text-white"}`}>{value}</div>
    </div>
  );
}

function ContactRow({ icon: Icon, value, href }) {
  const content = (
    <span className="flex items-center gap-1.5 text-white">
      <Icon size={11} weight="bold" className="text-white/40" />
      {value}
    </span>
  );
  return href ? <a href={href} className="hover:text-[#FACC15]">{content}</a> : content;
}

function freqLabel(f) {
  return { weekly: "semanal", biweekly: "quincenal", monthly: "mensual" }[f] || f;
}


// ============================================================ ReceivableFormDialog
function ReceivableFormDialog({ editing, onClose, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState(() => editing ? {
    client_name: editing.client_name || "",
    client_phone: editing.client_phone || "",
    client_email: editing.client_email || "",
    vehicle_year: editing.vehicle_year || "",
    vehicle_make: editing.vehicle_make || "",
    vehicle_model: editing.vehicle_model || "",
    vehicle_vin: editing.vehicle_vin || "",
    sale_date: editing.sale_date || "",
    sale_price: editing.sale_price ?? 0,
    down_payment: editing.down_payment ?? 0,
    agreed_payment: editing.agreed_payment ?? 0,
    payment_frequency: editing.payment_frequency || "monthly",
    first_due_date: editing.first_due_date || "",
    term_months: editing.term_months ?? 36,
    apr: editing.apr ?? "",
    contract_number: editing.contract_number || "",
    general_notes: editing.general_notes || "",
  } : {
    client_name: "", client_phone: "", client_email: "",
    vehicle_year: "", vehicle_make: "", vehicle_model: "", vehicle_vin: "",
    sale_date: new Date().toISOString().slice(0, 10),
    sale_price: 0, down_payment: 0, agreed_payment: 0,
    payment_frequency: "monthly", first_due_date: "",
    term_months: 36, apr: "", contract_number: "", general_notes: "",
  });

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const save = async () => {
    if (!f.client_name.trim()) { toast.error("Nombre del cliente requerido"); return; }
    setSaving(true);
    try {
      // Helpers:
      //   _str: return trimmed string (or '' if user cleared it — backend Optional[str]
      //         accepts '' and blanks the field so the user CAN clear a value).
      //   _num: parse to number; return null when blank/NaN so the backend can either
      //         (a) receive `null` and keep the field null, or (b) fall back to a default.
      //   _int: same as _num but integer.
      const _str = (v) => (v == null ? "" : String(v).trim());
      const _num = (v) => {
        if (v === "" || v == null) return null;
        const n = parseFloat(v);
        return Number.isFinite(n) ? n : null;
      };
      const _int = (v) => {
        if (v === "" || v == null) return null;
        const n = parseInt(v, 10);
        return Number.isFinite(n) ? n : null;
      };
      const payload = {
        // Strings + dates: always send (empty string clears them on the backend)
        client_name:       f.client_name.trim(),
        client_phone:      _str(f.client_phone),
        client_email:      _str(f.client_email),
        vehicle_make:      _str(f.vehicle_make),
        vehicle_model:     _str(f.vehicle_model),
        vehicle_vin:       _str(f.vehicle_vin),
        sale_date:         _str(f.sale_date),
        first_due_date:    _str(f.first_due_date),
        contract_number:   _str(f.contract_number),
        general_notes:     _str(f.general_notes),
        payment_frequency: f.payment_frequency || "monthly",
        // Numeric required-with-default: coerce to 0 / 36 when the user blanks them
        sale_price:     _num(f.sale_price)     ?? 0,
        down_payment:   _num(f.down_payment)   ?? 0,
        agreed_payment: _num(f.agreed_payment) ?? 0,
        term_months:    _int(f.term_months)    ?? 36,
        // Truly optional numerics: drop when blank (backend uses Optional[float])
        // We can't SEND null (backend excludes it) so we simply omit the key.
        ...(_int(f.vehicle_year) != null ? { vehicle_year: _int(f.vehicle_year) } : {}),
        ...(_num(f.apr)          != null ? { apr: _num(f.apr) } : {}),
      };
      if (editing) {
        await api.put(`/finance/receivables/${editing.receivable_id}`, payload);
        toast.success("Expediente actualizado");
      } else {
        await api.post("/finance/receivables", payload);
        toast.success("Cliente creado");
      }
      onSaved?.();
    } catch (err) {
      const status = err.response?.status;
      let detail = err.response?.data?.detail || err.response?.data?.message || err.message;
      if (Array.isArray(detail)) {
        detail = detail.map((d) => `${(d.loc || []).slice(-1)[0] || "?"}: ${d.msg}`).join(" · ");
      }
      // eslint-disable-next-line no-console
      console.error("[Receivable save failed]", { status, detail, err });
      toast.error(`No se pudo guardar${status ? ` (${status})` : ""}: ${detail || "error desconocido"}`);
    } finally { setSaving(false); }
  };

  return (
    <div
      data-testid="rcv-form-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl max-h-[92vh] overflow-hidden rounded-2xl border border-white/10 bg-[#0F1017] shadow-2xl flex flex-col"
      >
        <div className="p-4 border-b border-white/8">
          <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#FACC15]">
            {editing ? "EDITAR" : "NUEVO"} · EXPEDIENTE
          </div>
          <div className="text-lg font-heading font-bold text-white mt-0.5">
            {editing ? "Actualizar cliente" : "Nuevo cliente por cobrar"}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          <Section title="Cliente">
            <Field label="Nombre completo *" value={f.client_name} onChange={(v) => set("client_name", v)} testid="rcv-form-client-name" />
            <Field label="Teléfono"          value={f.client_phone} onChange={(v) => set("client_phone", v)} />
            <Field label="Email"             value={f.client_email} onChange={(v) => set("client_email", v)} type="email" />
          </Section>
          <Section title="Vehículo">
            <Field label="Año"    value={f.vehicle_year}  onChange={(v) => set("vehicle_year", v)} type="number" />
            <Field label="Marca"  value={f.vehicle_make}  onChange={(v) => set("vehicle_make", v)} />
            <Field label="Modelo" value={f.vehicle_model} onChange={(v) => set("vehicle_model", v)} />
            <Field label="VIN"    value={f.vehicle_vin}   onChange={(v) => set("vehicle_vin", v)} />
          </Section>
          <Section title="Contrato">
            <Field label="Fecha venta"      value={f.sale_date}       onChange={(v) => set("sale_date", v)} type="date" />
            <Field label="Precio venta"     value={f.sale_price}      onChange={(v) => set("sale_price", v)} type="number" />
            <Field label="Enganche"         value={f.down_payment}    onChange={(v) => set("down_payment", v)} type="number" />
            <Field label="Pago pactado"     value={f.agreed_payment}  onChange={(v) => set("agreed_payment", v)} type="number" />
            <Field label="Frecuencia"       value={f.payment_frequency} onChange={(v) => set("payment_frequency", v)} type="select"
              options={[["monthly","Mensual"],["biweekly","Quincenal"],["weekly","Semanal"]]} />
            <Field label="Primer vencimiento" value={f.first_due_date} onChange={(v) => set("first_due_date", v)} type="date" />
            <Field label="Plazo (# pagos)"  value={f.term_months}     onChange={(v) => set("term_months", v)} type="number" />
            <Field label="APR %"            value={f.apr}             onChange={(v) => set("apr", v)} type="number" />
            <Field label="# Contrato"       value={f.contract_number} onChange={(v) => set("contract_number", v)} />
          </Section>
          <div>
            <div className="text-[9px] font-mono uppercase tracking-widest text-white/40 mb-1">Notas</div>
            <textarea
              value={f.general_notes}
              onChange={(e) => set("general_notes", e.target.value)}
              rows={2}
              className="w-full bg-black/40 border border-white/10 rounded px-2 py-1.5 text-[12px] text-white outline-none focus:border-[#FACC15]/40"
            />
          </div>
        </div>
        <div className="p-3 border-t border-white/8 flex items-center justify-end gap-2 bg-black/20">
          <button onClick={onClose} className="px-3 py-2 text-xs font-mono uppercase tracking-widest text-white/60 hover:text-white">Cancelar</button>
          <button
            data-testid="rcv-form-save"
            onClick={save}
            disabled={saving}
            className="on-solid px-4 py-2 rounded-lg bg-[#FACC15] hover:bg-[#EAB308] disabled:opacity-40 text-slate-900 font-heading font-bold text-xs"
          >
            {saving ? "Guardando..." : editing ? "Actualizar" : "Crear expediente"}
          </button>
        </div>
      </div>
    </div>
  );
}


// ============================================================ PaymentFormDialog
function PaymentFormDialog({ receivable, editing, onClose, onSaved }) {
  const [saving, setSaving] = useState(false);
  // savedPayment holds the server response after a successful save; when set,
  // the dialog flips to the "Recibo listo" screen with a Copiar link + Ver PDF.
  const [savedPayment, setSavedPayment] = useState(null);
  const [f, setF] = useState(() => editing ? {
    payment_date: editing.payment_date || new Date().toISOString().slice(0, 10),
    received_amount: editing.received_amount ?? 0,
    scheduled_amount: editing.scheduled_amount ?? (receivable.agreed_payment || 0),
    extra_charge: editing.extra_charge ?? 0,
    method: editing.method || "Zelle",
    reference: editing.reference || "",
    notes: editing.notes || "",
  } : {
    payment_date: new Date().toISOString().slice(0, 10),
    received_amount: receivable.agreed_payment || 0,
    scheduled_amount: receivable.agreed_payment || 0,
    extra_charge: 0,
    method: "Zelle",
    reference: "",
    notes: "",
  });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const save = async () => {
    if (!f.payment_date) { toast.error("Fecha requerida"); return; }
    setSaving(true);
    try {
      const payload = {
        ...f,
        received_amount: parseFloat(f.received_amount) || 0,
        scheduled_amount: parseFloat(f.scheduled_amount) || 0,
        extra_charge: parseFloat(f.extra_charge) || 0,
      };
      let payment;
      if (editing) {
        const { data } = await api.put(
          `/finance/receivables/${receivable.receivable_id}/payments/${editing.payment_id}`,
          payload,
        );
        payment = data;
        toast.success("Pago actualizado");
      } else {
        const { data } = await api.post(
          `/finance/receivables/${receivable.receivable_id}/payments`,
          payload,
        );
        payment = data;
        toast.success("Pago registrado");
      }
      // Refresh the parent list, but keep the dialog open on the receipt screen
      onSaved?.({ keepOpen: true });
      setSavedPayment(payment);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo guardar");
    } finally { setSaving(false); }
  };

  return (
    <div
      data-testid="rcv-payment-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="force-dark-scope w-full max-w-md rounded-2xl border border-white/10 bg-[#0F1017] shadow-2xl"
      >
        {savedPayment ? (
          <PaymentReceiptStep
            receivable={receivable}
            payment={savedPayment}
            onClose={onClose}
          />
        ) : (
          <>
            <div className="p-4 border-b border-white/8">
              <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#FACC15]">
                {editing ? `EDITAR PAGO #${editing.seq} · ` : "REGISTRAR PAGO · "}{receivable.client_name}
              </div>
              <div className="text-sm font-heading font-bold text-white mt-0.5">
                Balance actual: {fmtMoney(receivable.current_balance)}
              </div>
            </div>
            <div className="p-4 grid grid-cols-2 gap-3">
              <Field label="Fecha del pago" value={f.payment_date} onChange={(v) => set("payment_date", v)} type="date" testid="rcv-payform-date" />
              <Field label="Método"         value={f.method}       onChange={(v) => set("method", v)} type="select"
                     options={[["Zelle","Zelle"],["Efectivo","Efectivo"],["Transferencia","Transferencia"],["Cheque","Cheque"],["Tarjeta","Tarjeta"]]} />
              <Field label="Monto recibido *" value={f.received_amount} onChange={(v) => set("received_amount", v)} type="number" testid="rcv-payform-amount" />
              <Field label="Cargo / interés" value={f.extra_charge}    onChange={(v) => set("extra_charge", v)} type="number" />
              <Field label="Referencia" value={f.reference}    onChange={(v) => set("reference", v)} span={2} />
              <div className="col-span-2">
                <div className="text-[9px] font-mono uppercase tracking-widest text-white/40 mb-1">Notas</div>
                <textarea
                  value={f.notes}
                  onChange={(e) => set("notes", e.target.value)}
                  rows={2}
                  className="w-full bg-black/40 border border-white/10 rounded px-2 py-1.5 text-[12px] text-white outline-none focus:border-[#FACC15]/40"
                />
              </div>
            </div>
            <div className="p-3 border-t border-white/8 flex items-center justify-end gap-2 bg-black/20">
              <button onClick={onClose} className="px-3 py-2 text-xs font-mono uppercase tracking-widest text-white/60 hover:text-white">Cancelar</button>
              <button
                data-testid="rcv-payform-save"
                onClick={save}
                disabled={saving}
                className="on-solid px-4 py-2 rounded-lg bg-emerald-700 hover:bg-emerald-800 disabled:opacity-40 text-white font-heading font-bold text-xs"
              >
                {saving ? "Guardando..." : editing ? "Guardar cambios" : "Registrar pago"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}


// ============================================================ PaymentReceiptStep
// Shown after a payment is saved: mints a public share link and offers
// Copy link + Download PDF actions so the owner can send the client their
// receipt without leaving the flow.
function PaymentReceiptStep({ receivable, payment, onClose }) {
  const [state, setState] = useState({ loading: true, link: null, error: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post(
          `/finance/receivables/${receivable.receivable_id}/payments/${payment.payment_id}/receipt-link`,
        );
        if (!cancelled) setState({ loading: false, link: data, error: null });
      } catch (err) {
        if (!cancelled) setState({
          loading: false, link: null,
          error: err.response?.data?.detail || "No se pudo generar el enlace",
        });
      }
    })();
    return () => { cancelled = true; };
  }, [receivable.receivable_id, payment.payment_id]);

  const copyLink = () => {
    if (!state.link?.share_url) return;
    navigator.clipboard.writeText(state.link.share_url).then(
      () => toast.success("Enlace copiado — pégalo en WhatsApp / SMS / email"),
      () => toast.error("No se pudo copiar"),
    );
  };

  const pdfUrl = state.link
    ? `${API}/public/receipt/${state.link.token}/pdf`
    : `${API}/finance/receivables/${receivable.receivable_id}/payments/${payment.payment_id}/receipt.pdf`;

  return (
    <div data-testid="rcv-payform-receipt">
      {/* Header */}
      <div className="p-4 border-b border-white/8 flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center shrink-0">
          <CheckCircle size={20} weight="fill" className="text-emerald-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-emerald-300">
            RECIBO LISTO · {receivable.client_name}
          </div>
          <div className="text-lg font-heading font-bold text-white mt-0.5">
            Pago registrado por {fmtMoney(payment.received_amount)}
          </div>
          <div className="text-[10px] font-mono text-white/50 mt-0.5">
            Comparte este enlace con el cliente para que tenga su comprobante oficial.
          </div>
        </div>
      </div>

      {/* Link block */}
      <div className="p-4 space-y-3">
        {state.loading ? (
          <div className="flex items-center gap-2 text-[11px] font-mono text-white/50 py-3">
            <ArrowClockwise size={13} weight="bold" className="animate-spin" />
            Generando enlace seguro...
          </div>
        ) : state.error ? (
          <div className="text-[12px] font-mono text-red-300">
            {state.error}
          </div>
        ) : (
          <>
            <div className="rounded-lg border border-white/8 bg-black/30 p-3">
              <div className="text-[9px] font-mono uppercase tracking-widest text-white/40 mb-1">
                Enlace del recibo · válido 90 días
              </div>
              <div
                data-testid="rcv-payform-receipt-url"
                className="text-[11px] font-mono text-emerald-300 break-all leading-relaxed"
              >
                {state.link.share_url}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <button
                data-testid="rcv-payform-receipt-copy"
                onClick={copyLink}
                className="on-solid flex items-center justify-center gap-2 rounded-lg bg-[#FACC15] hover:bg-[#EAB308] text-slate-900 font-heading font-bold py-2.5 text-[12px]"
              >
                <Copy size={14} weight="fill" />
                Copiar link
              </button>
              <a
                data-testid="rcv-payform-receipt-pdf"
                href={pdfUrl}
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-center gap-2 rounded-lg border border-white/10 bg-black/30 hover:bg-black/50 text-white/80 font-heading font-bold py-2.5 text-[12px]"
              >
                <FilePdf size={14} weight="bold" />
                Ver PDF
              </a>
            </div>

            <div className="text-[10px] font-mono text-white/40 text-center pt-1">
              El cliente abre el enlace y ve un recibo web + PDF descargable.
            </div>
          </>
        )}
      </div>

      <div className="p-3 border-t border-white/8 flex items-center justify-end gap-2 bg-black/20">
        <button
          data-testid="rcv-payform-receipt-close"
          onClick={onClose}
          className="on-solid px-4 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-white/80 font-heading font-bold text-xs"
        >
          Cerrar
        </button>
      </div>
    </div>
  );
}


// ============================================================ Reusable primitives
function Section({ title, children }) {
  return (
    <div>
      <div className="text-[9px] font-mono uppercase tracking-widest text-white/40 mb-2">{title}</div>
      <div className="grid grid-cols-2 gap-3">{children}</div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", options, span = 1, testid }) {
  return (
    <div className={span === 2 ? "col-span-2" : ""}>
      <div className="text-[9px] font-mono uppercase tracking-widest text-white/40 mb-1">{label}</div>
      {type === "select" ? (
        <select
          data-testid={testid}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-black/40 border border-white/10 rounded px-2 py-1.5 text-[12px] text-white outline-none focus:border-[#FACC15]/40"
        >
          {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      ) : (
        <input
          data-testid={testid}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-black/40 border border-white/10 rounded px-2 py-1.5 text-[12px] text-white outline-none focus:border-[#FACC15]/40"
        />
      )}
    </div>
  );
}


// ============================================================ ImportXlsxDialog
function ImportXlsxDialog({ onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [replace, setReplace] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const upload = async () => {
    if (!file) { toast.error("Selecciona un archivo .xlsx"); return; }
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        `/finance/receivables/import-xlsx?replace=${replace}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setResult(data);
      toast.success(`${data.receivables_inserted} expedientes + ${data.payments_inserted} pagos importados`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al importar");
    } finally { setUploading(false); }
  };

  const finish = () => { onImported?.(); };

  return (
    <div
      data-testid="rcv-import-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg rounded-2xl border border-white/10 bg-[#0F1017] shadow-2xl flex flex-col max-h-[92vh]"
      >
        <div className="p-4 border-b border-white/8">
          <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-emerald-400">
            IMPORTAR · EXCEL
          </div>
          <div className="text-lg font-heading font-bold text-white mt-0.5">
            Cargar cartera desde xlsx
          </div>
          <div className="text-[11px] font-mono text-white/50 mt-1">
            Formato esperado: una hoja por cliente con secciones INFORMACIÓN DEL CLIENTE / VEHÍCULO / CONTRATO / REGISTRO DE PAGOS. Cada hoja se convierte en un expediente.
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {/* File dropzone */}
          <label
            data-testid="rcv-import-dropzone"
            className={`block rounded-xl border-2 border-dashed p-6 text-center cursor-pointer transition ${
              file ? "border-emerald-500/40 bg-emerald-500/6" : "border-white/15 hover:border-white/30 bg-black/20"
            }`}
          >
            <UploadSimple size={28} weight="fill" className="mx-auto mb-2 text-emerald-400" />
            <div className="text-[12px] font-heading font-bold text-white mb-1">
              {file ? file.name : "Arrastra o haz clic para seleccionar"}
            </div>
            <div className="text-[10px] font-mono text-white/40">
              {file ? `${(file.size / 1024).toFixed(1)} KB · listo para importar` : "Formato .xlsx · máximo 20 MB"}
            </div>
            <input
              data-testid="rcv-import-file"
              type="file"
              accept=".xlsx"
              className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
          </label>

          <label className="flex items-center gap-2 text-[11px] font-mono text-white/70 cursor-pointer">
            <input
              data-testid="rcv-import-replace"
              type="checkbox"
              checked={replace}
              onChange={(e) => setReplace(e.target.checked)}
              className="w-4 h-4 rounded accent-red-500"
            />
            <span>
              <span className="text-red-300 font-bold">Reemplazar</span> todos los expedientes existentes antes de importar (irreversible)
            </span>
          </label>

          {/* Result summary */}
          {result && (
            <div data-testid="rcv-import-result" className="rounded-lg border border-emerald-500/30 bg-emerald-500/8 p-3">
              <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-300 mb-2">
                Importación completada
              </div>
              <div className="text-[12px] text-white space-y-1">
                <div>✓ <b>{result.receivables_inserted}</b> expedientes creados</div>
                <div>✓ <b>{result.payments_inserted}</b> pagos registrados</div>
                {result.sheets_skipped.length > 0 && (
                  <div className="text-white/50 text-[10px]">
                    Omitidas ({result.sheets_skipped.length}): {result.sheets_skipped.join(", ")}
                  </div>
                )}
              </div>
              {result.detail.filter((d) => d.ok).length > 0 && (
                <div className="mt-2 border-t border-white/10 pt-2 space-y-1">
                  {result.detail.filter((d) => d.ok).map((d) => (
                    <div key={d.sheet} className="text-[10px] font-mono text-white/60 flex items-center gap-2">
                      <CheckCircle size={10} weight="fill" className="text-emerald-400" />
                      <span className="flex-1">{d.client_name} — {d.vehicle}</span>
                      <span className="text-white/40">{d.payments} pagos</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="p-3 border-t border-white/8 flex items-center justify-between gap-2 bg-black/20">
          {result ? (
            <button
              data-testid="rcv-import-finish"
              onClick={finish}
              className="on-solid ml-auto px-4 py-2 rounded-lg bg-emerald-700 hover:bg-emerald-800 text-white font-heading font-bold text-xs"
            >
              Ver expedientes
            </button>
          ) : (
            <>
              <button onClick={onClose} className="px-3 py-2 text-xs font-mono uppercase tracking-widest text-white/60 hover:text-white">Cancelar</button>
              <button
                data-testid="rcv-import-upload"
                onClick={upload}
                disabled={!file || uploading}
                className="on-solid px-4 py-2 rounded-lg bg-emerald-700 hover:bg-emerald-800 disabled:opacity-40 text-white font-heading font-bold text-xs flex items-center gap-1"
              >
                {uploading ? <ArrowClockwise size={12} weight="bold" className="animate-spin" /> : <UploadSimple size={12} weight="bold" />}
                {uploading ? "Importando..." : "Importar"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


// ============================================================ DetectFromTxDialog
function DetectFromTxDialog({ onClose, onCreated }) {
  const [pattern, setPattern] = useState("Sergio");
  const [minAmount, setMinAmount] = useState(50);
  const [carLabel, setCarLabel] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  const runPreview = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/finance/receivables/detect-from-transactions", {
        pattern,
        min_amount: parseFloat(minAmount) || 0,
        auto_create: false,
      });
      setPreview(data.preview);
    } catch (err) {
      toast.error("No se pudo escanear");
    } finally { setLoading(false); }
  };

  const create = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/finance/receivables/detect-from-transactions", {
        pattern,
        min_amount: parseFloat(minAmount) || 0,
        auto_create: true,
        default_car: carLabel || null,
      });
      toast.success(`Expediente creado con ${data.created.payments_created} pagos`);
      onCreated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo crear");
    } finally { setCreating(false); }
  };

  return (
    <div
      data-testid="rcv-detect-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-xl rounded-2xl border border-white/10 bg-[#0F1017] shadow-2xl flex flex-col max-h-[92vh]"
      >
        <div className="p-4 border-b border-white/8">
          <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#FACC15]">
            DETECTAR · DESDE TRANSACCIONES
          </div>
          <div className="text-lg font-heading font-bold text-white mt-0.5">
            Auto-crear expediente desde depósitos
          </div>
          <div className="text-[11px] font-mono text-white/50 mt-1">
            Escanea tus transacciones (Zelle, Cash App, transferencias) que coincidan con un patrón y crea automáticamente un expediente con esas transacciones como historial de pagos.
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Patrón (nombre / descripción)" value={pattern} onChange={setPattern} testid="rcv-detect-pattern" />
            <Field label="Monto mínimo" value={minAmount} onChange={setMinAmount} type="number" />
          </div>
          <Field label="Carro (opcional — ej. 'GMC Sierra 2021')" value={carLabel} onChange={setCarLabel} span={2} />

          <button
            data-testid="rcv-detect-preview"
            onClick={runPreview}
            disabled={loading}
            className="w-full flex items-center justify-center gap-1.5 rounded-lg border border-white/10 bg-black/30 hover:bg-black/50 text-white/80 font-mono uppercase tracking-widest px-3 py-2 text-[10px]"
          >
            {loading ? <ArrowClockwise size={11} weight="bold" className="animate-spin" /> : <MagnifyingGlass size={11} weight="bold" />}
            {loading ? "Buscando..." : "Vista previa"}
          </button>

          {preview && (
            <div data-testid="rcv-detect-preview-result" className="rounded-lg border border-white/8 bg-black/20 p-3">
              <div className="text-[10px] font-mono uppercase tracking-widest text-white/40 mb-2">
                Resultado: {preview.count} coincidencias · ${preview.total_amount.toFixed(2)}
              </div>
              {preview.count === 0 ? (
                <div className="text-[11px] text-white/40 text-center py-4">
                  No se encontraron transacciones con ese patrón.
                </div>
              ) : (
                <div className="max-h-48 overflow-y-auto space-y-1">
                  {preview.matches.slice(0, 20).map((m) => (
                    <div key={m.transaction_id} className="flex items-center gap-2 text-[10px] font-mono">
                      <span className="text-white/40 w-20">{m.date}</span>
                      <span className="flex-1 text-white truncate">{m.merchant || m.description}</span>
                      <span className="text-emerald-300 tabular-nums">+${m.amount.toFixed(2)}</span>
                    </div>
                  ))}
                  {preview.matches.length > 20 && (
                    <div className="text-[9px] text-white/30 text-center pt-1">
                      + {preview.matches.length - 20} más
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="p-3 border-t border-white/8 flex items-center justify-end gap-2 bg-black/20">
          <button onClick={onClose} className="px-3 py-2 text-xs font-mono uppercase tracking-widest text-white/60 hover:text-white">Cancelar</button>
          <button
            data-testid="rcv-detect-create"
            onClick={create}
            disabled={!preview || preview.count === 0 || creating}
            className="on-solid px-4 py-2 rounded-lg bg-[#FACC15] hover:bg-[#EAB308] disabled:opacity-40 text-slate-900 font-heading font-bold text-xs flex items-center gap-1"
          >
            {creating ? <ArrowClockwise size={12} weight="bold" className="animate-spin" /> : <Plus size={12} weight="bold" />}
            Crear expediente
          </button>
        </div>
      </div>
    </div>
  );
}
